import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Stamp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import ToolSidebar from '../components/ToolSidebar';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import '../styles/StampMakerPage.css';
import '../styles/HomePage.css';

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const StampMakerPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const seoDefaults = {
    title: 'Free Online Stamp Maker — Create Custom Digital Rubber Stamps | Registan Tourism',
    description: 'Create professional digital rubber stamps online for free. Design round, rectangular, or custom company stamps for documents, invoices, and official papers. No signup required — instant download in PNG format.',
    keywords: 'stamp maker, online stamp creator, digital rubber stamp, custom stamp generator, company stamp maker, free stamp tool, document stamp, official stamp creator, business stamp design'
  };
  const seo = useSEO('stampmaker', seoDefaults);
  const seoTitle = seo?.title || seoDefaults.title;
  const seoDesc = seo?.description || seoDefaults.description;
  const seoKeywords = seo?.keywords || seoDefaults.keywords;
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="stampmaker-page" data-testid="stampmaker-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href="https://registantours.com/stampmaker" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content="https://registantours.com/stampmaker" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/stampmaker" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      {/* Main Content */}
      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="stampmaker-main tool-page-content">
          <div className="stampmaker-hero">
          <h1 data-testid="stampmaker-h1">{t('stampPageH1') || 'Free Online Stamp Maker — Create Custom Digital Rubber Stamps'}</h1>
          <h2 data-testid="stampmaker-h2">{t('stampPageH2') || 'Design Professional Company Stamps for Documents, Invoices & Official Papers'}</h2>
          <p className="stampmaker-description">
            {t('stampPageText') || 'Create high-quality digital rubber stamps instantly with our free online stamp maker tool. Whether you need a round company seal, a rectangular approval stamp, or a custom signature stamp for your business documents, our tool lets you design and download professional stamps in seconds. Perfect for freelancers, small businesses, and enterprises in the UAE and worldwide. Choose from multiple shapes, add your company name, registration number, and logo — then download your stamp as a transparent PNG ready to use on invoices, contracts, and official paperwork. No registration required, completely free.'}
          </p>
        </div>

        <div className="stampmaker-coming-soon" data-testid="stampmaker-tool-area">
          <Stamp size={64} className="coming-icon" />
          <h3>{t('stampToolTitle') || 'Stamp Maker Tool'}</h3>
          <p>{t('stampComingSoon') || "Coming Soon — We're building a powerful stamp creation tool for you."}</p>
        </div>
      </main>
      </div>

      <SiteFooter />
      <MobileBottomNav />
    </div>
  );
};

export default StampMakerPage;
